/* eslint-disable no-console */
import { create } from 'zustand';
import { isEmpty } from 'lodash';
import mixpanel from 'mixpanel-browser';

import { client as apolloClient } from '../util/apolloClient';
import { client as api2Client } from '../util/api2Client';
import {
  listCountriesQuery,
  quickSearchProjectsPaginatedQuery,
  quickSearchProjectsQuery,
  trendingTopicsQuery,
} from '../graphql/search';
import {
  followMutationV2,
  unfollowMutationV2,
  getFollowersQuery,
  getFollowersQueryV2,
} from '../graphql/follow';
import { trackFollow, trackUnfollow } from '../util/tracking/follow';
import { topCharitiesQuery } from 'graphql/charity';
import { MATCH_COMPANY_IDS } from 'util/constants';
import { eventsQuery } from 'graphql/event';

const useSearchStore = create((set, get) => ({
  // State
  loading: false,
  loadingMore: false,
  results: [],
  page: 1,
  pageSize: 50,
  currentSearch: {
    query: '',
    filter: 'none',
    location: '',
    country: 'United States',
    topics: [],
    categories: [],
  },
  activeSearch: {},
  countryWhitelist: ['US'],
  topics: [],
  topicEvents: [],
  categories: [],
  activeTopic: '',
  activeCategory: '',
  charityFollowers: [],
  loadingFollowers: false,
  matchCompanyId: null,
  loadingTopicEvents: false,

  setMatchCompanyId: (companyId) => set({ matchCompanyId: companyId }),
  setLoading: (loading) => set({ loading }),

  // Actions
  setPage: (page) => set({ page }),

  updateAndRunSearch: async (props) => {
    const { updateSearch, getCharities } = get();
    updateSearch(props);
    const charities = await getCharities();
    return charities;
  },

  updateSearch: (props) => {
    const { currentSearch } = get();
    const newSearch = { ...currentSearch, ...props };
    set({ currentSearch: newSearch });

    global.IS_LOCAL_OR_DEV && console.log('Current Search', newSearch);
  },

  getTopCharitiesByZip: async ({ zip }) => {
    const options = {
      variables: { zip, ignoreZip: false },
      query: topCharitiesQuery,
      fetchPolicy: 'no-cache',
      errorPolicy: global.IS_DEV ? 'all' : 'none',
    };

    try {
      set({ topCharitiesByZipLoading: true });
      const result = await api2Client.query(options);
      return result.data.topProjects;
    } catch (err) {
      console.error(err);
    } finally {
      set({ topCharitiesByZipLoading: false });
    }
  },

  fetchCharities: async ({
    query,
    location = '',
    country = 'United States',
    categories = [],
    filter = 'none',
    topics = [],
    includeCompanyTopics = false,
    page = 1,
    pageSize = 50,
  }) => {
    const { topics: _topics, matchCompanyId } = get();

    let companyTopics = [];

    if (includeCompanyTopics) {
      companyTopics = _topics
        .filter((t) => t.companyId === matchCompanyId)
        .map((t) => t.name);
    }

    const variables = {
      query,
      location,
      country,
      topics: companyTopics.concat(topics),
      categories,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    };

    mixpanel.track('Charity Search', {
      channel: 'web',
      query: variables.query,
      location_filter: variables.location,
      country_filter: variables.country,
      topics_filter: variables.topics,
    });

    if (filter !== 'none') {
      variables.categories = filter;
    }

    const options = {
      variables,
      query: quickSearchProjectsPaginatedQuery,
      fetchPolicy: 'no-cache',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    let results;
    let totalCount;

    if (
      !variables.query &&
      isEmpty(variables.categories) &&
      isEmpty(variables.topics) &&
      !variables.location &&
      variables.country === 'United States' &&
      matchCompanyId !== MATCH_COMPANY_IDS.ALTMAN_SOLON
    ) {
      const { getTopCharitiesByZip } = get();
      const topCharities = await getTopCharitiesByZip({
        zip: variables.location,
      });

      results = topCharities.map((charity) => ({
        avatar: charity.avatarUrls,
        city: charity.project.city,
        description: charity.project.description,
        ein: charity.project.ein,
        featuredFollowers: charity.project.topSubscriptionEntities || [],
        followerCount: charity.project.totalFollowers,
        id: charity.project.id,
        isSelfFollowing: charity.currentEntityIsFollowing,
        name: charity.name,
        state: charity.project.state,
      }));
    } else {
      const res = await api2Client.query(options);
      totalCount = res.data.quickSearchProjectsPaginated.totalCount;
      results = res.data.quickSearchProjectsPaginated.projects.map(
        (charity) => ({
          avatar: charity.avatarUrls,
          city: charity.city,
          description: charity.description,
          ein: charity.ein,
          featuredFollowers: charity.featuredFollowers || [],
          followerCount: charity.totalFollowers,
          id: charity.id,
          isSelfFollowing: charity.currentEntityIsFollowing,
          name: charity.name,
          state: charity.state,
          topics: charity.topics,
        }),
      );
    }

    return {
      results,
      totalCount,
    };
  },

  getCharities: async (loadMore = false) => {
    const { currentSearch, fetchCharities, page, pageSize } = get();

    if (loadMore) {
      set({ page: page + 1, loadingMore: true });
    } else {
      set({
        page: 1,
        loading: true,
        activeSearch: {},
      });
    }

    try {
      const { results } = await fetchCharities({
        query: currentSearch.query,
        location: currentSearch.location,
        country: currentSearch.country,
        categories: currentSearch.categories,
        filter: currentSearch.filter,
        topics: currentSearch.topics,
        page: get().page,
        pageSize,
      });

      if (currentSearch.query) {
        const { clearActiveTopic, clearActiveCategory } = get();
        clearActiveTopic();
        clearActiveCategory();
      }

      if (loadMore) {
        set({ results: [...get().results, ...results] });
      } else {
        set({
          results,
          activeSearch: currentSearch,
          isError: false,
        });
      }

      return results;
    } catch (err) {
      console.error('err', err);
      set({ isError: true, error: err });
    } finally {
      set({ loading: false, loadingMore: false });
    }
  },

  getTopicEvents: async () => {
    set({ loadingTopicEvents: true });
    const { topics, matchCompanyId } = get();

    if (!matchCompanyId) {
      return [];
    }

    const topicIds = topics
      .filter((topic) => topic.companyId === matchCompanyId)
      .map((topic) => topic.id);

    const options = {
      variables: { topicIds, limit: 100, offset: 0 },
      query: eventsQuery,
      fetchPolicy: 'no-cache',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    try {
      const result = await api2Client.query(options);
      set({ topicEvents: result.data.events });
      return result.data.events;
    } catch (err) {
      console.error(err);
    } finally {
      set({ loadingTopicEvents: false });
    }
  },

  updateCharityFilters: (filters) => {
    set({ currentSearch: filters });
  },

  getCountryList: async () => {
    const options = {
      query: listCountriesQuery,
      fetchPolicy: 'no-cache',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    try {
      const result = await apolloClient.query(options);
      let usaIndex = result.data.listCountries.findIndex(
        (country) => country.name === 'United States',
      );
      const _listCountriesLength = result.data.listCountries.unshift(
        result.data.listCountries.splice(usaIndex, 1)[0],
      );
      set({ countryWhitelist: result.data.listCountries });
    } catch (err) {
      console.error(err);
    }
  },

  resetSearch: () => {
    set({
      currentSearch: { query: '', filter: 'none', location: '' },
      activeSearch: {},
      results: [],
    });
  },

  getTopics: async () => {
    const options = {
      query: trendingTopicsQuery,
      fetchPolicy: 'no-cache',
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    try {
      const result = await api2Client.query(options);
      const topics = result.data.trendingTopics;
      set({ topics });
    } catch (err) {
      console.error(err);
    }
  },

  getCompanyTopicCharityCount: () => {
    const { topics, matchCompanyId } = get();

    return topics
      .filter((t) => t.companyId === matchCompanyId)
      .reduce((acc, t) => acc + t.charityCount, 0);
  },

  getCategories: async () => {
    try {
      set({ categories: [] });
    } catch (err) {
      console.error(err);
    }
  },

  setActiveTopic: (topic) => {
    const {
      activeTopic,
      clearActiveCategory,
      clearActiveTopic,
      updateSearch,
      getCharities,
    } = get();

    clearActiveCategory();
    if (activeTopic === topic) {
      clearActiveTopic();
      updateSearch({ topics: [] });
      getCharities();
    } else {
      set({ activeTopic: topic });
      updateSearch({ topics: [topic] });
      getCharities();
    }
  },

  setActiveCategory: (category) => {
    const {
      activeCategory,
      clearActiveTopic,
      clearActiveCategory,
      updateSearch,
      getCharities,
    } = get();

    clearActiveTopic();
    if (activeCategory === category) {
      clearActiveCategory();
      updateSearch({ categories: [] });
      getCharities();
    } else {
      set({ activeCategory: category });
      updateSearch({ categories: [category] });
      getCharities();
    }
  },

  followCharity: ({ follow = true, actorContext, charityId }) => {
    const { toggleLocalCharityFollow } = get();

    // Only charities are listed in the search results for now, so only need to check context.charityId
    // This may change in the future
    toggleLocalCharityFollow(follow, charityId);

    if (follow) {
      trackFollow({ actorContext, targetContext: { charityId } });
    } else {
      trackUnfollow({ actorContext, targetContext: { charityId } });
    }

    let entityId = null;
    let entityType = null;
    if (charityId) {
      entityId = charityId;
      entityType = 'CHARITY';
    }

    const options = {
      variables: {
        entityId: parseInt(entityId),
        entityType,
      },
      mutation: follow ? followMutationV2 : unfollowMutationV2,
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    api2Client.mutate(options).catch((err) => {
      toggleLocalCharityFollow(!follow, charityId);

      if (global.IS_LOCAL_OR_DEV) {
        console.error(err);
      }
    });
  },

  toggleLocalFollow: (id, follow) => {
    const { charityFollowers } = get();
    const updatedFollowers = charityFollowers.map((entity) =>
      entity.id === id ? { ...entity, isSelfFollowing: follow } : entity,
    );
    set({ charityFollowers: updatedFollowers });
  },

  getFollowersForResult: async ({ targetContext, userContext }) => {
    set({ loadingFollowers: true, charityFollowers: [] });
    let options = {};

    if (userContext) {
      options = {
        variables: { targetContext, userContext },
        query: getFollowersQuery,
        errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
        fetchPolicy: 'no-cache',
      };

      try {
        const result = await apolloClient.query(options);
        const charityFollowers = result.data.getFollowers;
        set({ charityFollowers });
      } catch (err) {
        global.IS_LOCAL_OR_DEV && console.log('getFollowers error', err);
      }
    } else {
      options = {
        variables: { projectId: parseInt(targetContext?.charityId) },
        query: getFollowersQueryV2,
        errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
        fetchPolicy: 'no-cache',
      };

      try {
        const result = await api2Client.query(options);
        const charityFollowers = result.data.projectFollowers;
        set({ charityFollowers });
      } catch (err) {
        global.IS_LOCAL_OR_DEV && console.log('projectFollowers error', err);
      }
    }

    set({ loadingFollowers: false });
  },

  follow: ({ follow = true, targetContext, actorContext, targetId }) => {
    const { toggleLocalFollow } = get();

    if (follow) {
      trackFollow({ actorContext, targetContext });
    } else {
      trackUnfollow({ actorContext, targetContext });
    }
    toggleLocalFollow(targetId, follow);

    let entityId = null;
    let entityType = null;
    if (targetContext.userId) {
      entityId = targetContext.userId;
      entityType = 'USER';
    } else if (targetContext.charityId) {
      entityId = targetContext.charityId;
      entityType = 'CHARITY';
    } else if (targetContext.companyId) {
      entityId = targetContext.companyId;
      entityType = 'COMPANY';
    }

    const options = {
      variables: {
        entityId,
        entityType,
      },
      mutation: follow ? followMutationV2 : unfollowMutationV2,
      errorPolicy: global.IS_LOCAL_OR_DEV ? 'all' : 'none',
    };

    api2Client.mutate(options).catch((err) => {
      toggleLocalFollow(targetId, !follow);

      if (global.IS_LOCAL_OR_DEV) {
        console.error(err);
      }
    });
  },

  toggleLocalCharityFollow: (follow = true, targetId) => {
    const { results } = get();
    const updatedResults = results.map((result) =>
      result.id === targetId ? { ...result, isSelfFollowing: follow } : result,
    );
    set({ results: updatedResults });
  },

  clearActiveTopic: () => {
    set({ activeTopic: '' });
  },

  clearActiveCategory: () => {
    set({ activeCategory: '' });
  },

  onUnmount: () => {
    set({
      loading: true,
      loadingMore: false,
      page: 1,
      results: [],
      topics: [],
      charityFollowers: [],
      loadingFollowers: false,
    });
    // Reset search after setting other states
    const { resetSearch } = get();
    resetSearch();
  },
}));

export default useSearchStore;
